import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider, QueryFunctionContext, QueryKey, useIsFetching } from 'react-query';
import { Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Query plumbing for the Analytics page tabs (T-091).
 *
 * The recap RPCs are heavy (get_ads_pool_funnel & co. cost ~5 s of DB time each). With
 * react-query's defaults (staleTime 0, refetch on window focus) the Ads pool tab alone fired
 * 16 of them on mount and 16 more every time the admin tab regained focus, which starved the
 * 60-connection pool for every other admin screen on 2026-09-30. So, for these tabs only:
 *
 *  - their own QueryClient: cached 10 min, no refetch on focus/reconnect, one retry;
 *  - at most MAX_HEAVY recap RPCs in flight at once (runLimited), queued in call order;
 *  - panels below the fold fetch when scrolled near (useSeen);
 *  - a Refresh button re-runs the visible panels on demand.
 *
 * Same RPCs, same parameters: only when and how many at a time changes.
 */

const STALE_MS = 10 * 60 * 1000;
const MAX_HEAVY = 3;

/** A queued RPC whose panel unmounted before its turn: never sent. */
class SkippedError extends Error {
    constructor() { super('Skipped: the panel is no longer on screen'); }
}

export const analyticsQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: STALE_MS,
            cacheTime: 3 * STALE_MS,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchInterval: false,
            retry: (count, err) => !(err instanceof SkippedError) && count < 1,
        },
    },
});

export const AnalyticsQueryProvider = ({ children }: { children: ReactNode }) => (
    <QueryClientProvider client={analyticsQueryClient}>{children}</QueryClientProvider>
);

let active = 0;
const waiting: Array<() => void> = [];

/**
 * Runs `run` when one of the MAX_HEAVY slots is free. A finished call hands its slot straight
 * to the next waiter, so the cap holds even between the release and the waiter resuming.
 * If nothing is watching the query any more when its turn comes (tab switched), it is skipped.
 */
export const runLimited = async <T,>(queryKey: QueryKey, run: () => Promise<T>): Promise<T> => {
    if (active < MAX_HEAVY) active++;
    else await new Promise<void>(resolve => waiting.push(resolve));
    try {
        const q = analyticsQueryClient.getQueryCache().find(queryKey, { exact: true });
        if (q && q.getObserversCount() === 0) throw new SkippedError();
        return await run();
    } finally {
        const next = waiting.shift();
        if (next) next();
        else active--;
    }
};

/** Wraps a query function so it goes through runLimited. */
export const limited = <T,>(run: () => Promise<T>) =>
    ({ queryKey }: QueryFunctionContext): Promise<T> => runLimited(queryKey, run);

/**
 * `[ref, seen]`: seen turns true once the element comes within `rootMargin` of the viewport
 * and stays true. Use as `enabled: seen` on a panel's queries.
 */
export const useSeen = (rootMargin = '300px'): [(el: Element | null) => void, boolean] => {
    const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined');
    const observer = useRef<IntersectionObserver | null>(null);
    const ref = useCallback((el: Element | null) => {
        observer.current?.disconnect();
        observer.current = null;
        if (!el || seen) return;
        observer.current = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                observer.current?.disconnect();
                setSeen(true);
            }
        }, { rootMargin });
        observer.current.observe(el);
    }, [seen, rootMargin]);
    useEffect(() => () => observer.current?.disconnect(), []);
    return [ref, seen];
};

/** Thin bar over a lazy section while its first load is running. */
export const SectionLoading = ({ loading }: { loading: boolean }) =>
    loading ? <LinearProgress sx={{ mb: 1 }} /> : null;

/**
 * The time a tab's numbers run up to. It is part of every query key, so Refresh (a new asOf)
 * refetches what is on screen while switching windows back and forth reuses the cache. It
 * outlives the tab's mount for STALE_MS, so leaving the tab and coming back hits the cache too.
 */
const asOfByTab = new Map<string, string>();
const nowIso = () => new Date().toISOString();
const freshAsOf = (tab: string): string => {
    const prev = asOfByTab.get(tab);
    if (prev && Date.now() - Date.parse(prev) < STALE_MS) return prev;
    const next = nowIso();
    asOfByTab.set(tab, next);
    return next;
};

export const useAsOf = (tab: string): [string, () => void] => {
    const [asOf, setAsOf] = useState(() => freshAsOf(tab));
    const refresh = useCallback(() => {
        const next = nowIso();
        asOfByTab.set(tab, next);
        setAsOf(next);
    }, [tab]);
    return [asOf, refresh];
};

export const daysBefore = (iso: string, days: number): string =>
    new Date(Date.parse(iso) - days * 86400000).toISOString();

export const RefreshButton = ({ asOf, onRefresh }: { asOf?: string; onRefresh: () => void }) => {
    const fetching = useIsFetching() > 0;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {asOf && (
                <Typography variant="caption" color="text.secondary">
                    As of {new Date(asOf).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
            )}
            <Button size="small" variant="outlined" onClick={onRefresh} disabled={fetching}
                startIcon={fetching ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}>
                Refresh
            </Button>
        </Box>
    );
};
